import { forwardRef, useCallback, useContext, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { isEmpty, compact, last, omit } from "lodash"
import classNames from "classnames"
import { GridRow } from "@mui/x-data-grid"
import { Box } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { ErrorBoundary } from "@sentry/react"
import OPTooltip from "components/material/tooltip"
import { useControllableState } from "hooks/use-controllable-state"
import { useRegisterDownloadData } from "hooks/use-register-download-data"
import { makeAssetViewDownloadData } from "connected-components/asset-view/helpers"
import { ASC, DESC } from "helpers/sorts"
import { LOADING, ERROR } from "helpers/data-statuses"
import { MetricPropType, IntradayPricingSummaryPropType } from "./prop-types"
import { getVisibleIntradayPricePrefix, isRowPricedOnPriorClose } from "./helpers/intraday-prior-row"
import { isVisibleRowIndex } from "./helpers/is-visible-row-index"
import { useContributorsGridBase, useTableData, useColumnDefinitions, useDataGridPersistence, useColumnDragImage, useCellValueFlash, useTradesResetKey, useGoToRowFlash, useHeaderLayout } from "./hooks"
import { TableFooter, TableHeader, LabelCell, MetricCell, DataGridErrorFallback, CustomColumnMenu, GoToFinderDataGrid, HeaderMaxLinesContext, CONTRIBUTORS_GRID_SX } from "./components"
import { DEFAULT_METRIC_COLUMN_WIDTH, MAX_COLUMN_WIDTH } from "./constants"
import { canContributorOpenDrilldown } from "./helpers/can-contributor-open-drilldown"
import { ColumnOrderContext } from "connected-components/asset-view/context/column-order"

import "./style.scss"
import { TOOLTIP_ENTER_DELAY_MS } from "helpers/constants"

const PERSISTED_STATE_KEYS_OMITTED_FOR_HYDRATION = ["columns", "focus", "tabIndex"]

const EMPTY_HIGHLIGHTED_ROW_IDS = []

const DrilldownTooltipRow = forwardRef(function DrilldownTooltipRow(props, ref) {
  const { viewLabel, onDrilldownRow, ...rowProps } = props
  const contributor = rowProps.row?.contributor
  const canDrilldown = !rowProps.row?.isAggregation && onDrilldownRow && contributor && canContributorOpenDrilldown(contributor)
  const title = canDrilldown && viewLabel
    ? `Click to view ${contributor.label} ${viewLabel} contributors`
    : ""
  const handleClick = canDrilldown
    ? (e) => onDrilldownRow(contributor.id, { clientX: e.clientX, clientY: e.clientY })
    : rowProps.onClick
  const cursorAnchorRef = useRef(null)
  const handleMouseEnter = useCallback((e) => {
    const { clientX, clientY } = e
    cursorAnchorRef.current = {
      getBoundingClientRect: () => ({ top: clientY, left: clientX, bottom: clientY, right: clientX, width: 0, height: 0 }),
    }
  }, [])
  return (
    <OPTooltip
      title={title}
      placement="bottom-start"
      enterDelay={TOOLTIP_ENTER_DELAY_MS}
      disableHoverListener={!canDrilldown}
      slotProps={{ popper: canDrilldown ? { anchorEl: () => cursorAnchorRef.current } : undefined }}
    >
      <GridRow ref={ref} {...rowProps} onClick={handleClick} onMouseEnter={handleMouseEnter} />
    </OPTooltip>
  )
})

DrilldownTooltipRow.propTypes = {
  viewLabel: PropTypes.string,
  onDrilldownRow: PropTypes.func,
}

/** Prepends the current-level summary row when it has values, followed by all contributor rows. */
function buildDownloadRows(currentLevelData, sortedContributorsArray) {
  return compact([currentLevelData && !isEmpty(currentLevelData.values) && currentLevelData, ...sortedContributorsArray])
}

function ContributorsDataGridComponent({
  status,
  data,
  metrics,
  disableCategories,
  groupByName,
  isPositionAttributeGroup,
  defaultSort,
  idLabel,
  classes,
  includeDrilldownArrows,
  downloadKey,
  downloadName,
  highlightedRowIds = EMPTY_HIGHLIGHTED_ROW_IDS,
  intradayPricingSummary = null,
  intradayPricePrefix = null,
  currency,
  accountCurrency,
  isCurrencyUnavailable,
  sortMetricId: propSortMetricId,
  onSortMetricIdChange,
  sortDirection: propSortDirection,
  onSortDirectionChange,
  tableNavigationLevels: propTableLevels,
  onTableNavigationChange,
  onSortChanged,
  onTableNavigationChanged,
  onRowClicked,
  onDrilldownRowClick,
  viewLabel,
  // Persistence props
  persistenceId,
  enablePersistence = true,
}) {
  const columnOrderContext = useContext(ColumnOrderContext)
  const columnOrder = columnOrderContext?.columnOrder ?? "custom"
  const onColumnOrderChange = columnOrderContext?.onColumnOrderChange

  const gridId = useMemo(() => {
    return persistenceId || downloadKey || "contributors-datagrid"
  }, [persistenceId, downloadKey])

  const {
    apiRef,
    initialState,
    isStateLoaded,
    savedCustomSort,
    savedCustomColumnOrder,
    handleSortChange: handlePersistenceSortChange,
  } = useDataGridPersistence(gridId, {
    isPersistenceEnabled: enablePersistence,
    onColumnOrderChange,
  })

  // Use saved custom sort if available, otherwise use props/defaults
  const initialSortMetricId = savedCustomSort?.field || propSortMetricId || defaultSort.path
  const initialSortDirection = savedCustomSort?.direction || propSortDirection || defaultSort.direction

  const [sortMetricId, setSortMetricId] = useControllableState(propSortMetricId, initialSortMetricId, onSortMetricIdChange)
  const [sortDirection, setSortDirection] = useControllableState(propSortDirection, initialSortDirection, onSortDirectionChange)

  const [selectedTableLevels, setSelectedTableLevels] = useControllableState(
    propTableLevels,
    [],
    onTableNavigationChange,
  )

  const handleSortChange = useCallback((field, direction) => {
    if (direction === null) {
      setSortMetricId(null)
      setSortDirection(null)
      onSortChanged?.(null, null)
      handlePersistenceSortChange(null, null)
    } else {
      setSortMetricId(field)
      setSortDirection(direction)
      onSortChanged?.(field, direction)
      handlePersistenceSortChange(field, direction)
    }
  }, [setSortMetricId, setSortDirection, onSortChanged, handlePersistenceSortChange])

  const tableDataProps = useMemo(() => ({
    data,
    metrics,
    disableCategories,
    selectedTableLevels,
    setSelectedTableLevels,
    sortMetricId,
    sortDirection,
    highlightedRowIds,
    onTableNavigationChanged,
    onRowClicked,
    columnOrder,
    status,
  }), [
    data, metrics, disableCategories, selectedTableLevels, setSelectedTableLevels, sortMetricId,
    sortDirection, highlightedRowIds, onTableNavigationChanged, onRowClicked, columnOrder, status,
  ])

  const {
    currentLevelData,
    parentLevelData,
    metricMetadata,
    metricCategories,
    sortedContributorsArray,
    rows,
    pinnedRows,
    isTopLevel,
    currentLevelLabel,
    sortArrowDirection,
    isAbsoluteSort,
    handleSetTableLevel,
    handleClickContributor,
  } = useTableData(tableDataProps)

  const visibleIntradayPricePrefix = useMemo(() => getVisibleIntradayPricePrefix(metricMetadata), [metricMetadata])

  const { flashingRowId, triggerFlash } = useGoToRowFlash()
  const [goToAnnouncement, setGoToAnnouncement] = useState("")
  const [goToAnnouncementKey, setGoToAnnouncementKey] = useState(0)

  const handleGoToRow = useCallback((match) => {
    const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(match.id)
    if (!isVisibleRowIndex(rowIndex)) return
    apiRef.current.scrollToIndexes({ rowIndex })
    apiRef.current.setCellFocus(match.id, "label")
    triggerFlash(match.id)
    setGoToAnnouncement(`Scrolled to ${match.label}`)
    setGoToAnnouncementKey((key) => key + 1)
  }, [apiRef, triggerFlash])

  const columnProps = useMemo(() => ({
    idLabel,
    metricMetadata,
    metricCategories,
    groupingEnabled: true,
    customGroups: [],
    disableCategories,
    dropdownTableTitle: groupByName,
    rootData: data,
    savedCustomColumnOrder,
    savedDataGridState: initialState,
    LabelCellComponent: LabelCell,
    intradayPricePrefix,
    currency,
    accountCurrency,
    isCurrencyUnavailable,
    MetricCellComponent: MetricCell,
    CustomHeaderComponent: TableHeader,
    columnOrder,
    sortMetricId,
    sortDirection,
    isAbsoluteSort,
    sortArrowDirection,
    handleClickContributor,
    includeDrilldownArrows,
    isTopLevel,
    handleSetTableLevel,
    onSortChange: handleSortChange,
    parentLevelOptions: parentLevelData?.children || {},
    currentGroupId: last(selectedTableLevels),
    data: currentLevelData,
    sortedContributorsArray,
    onDrilldownRowClick,
    onGoToRow: handleGoToRow,
  }), [
    idLabel, metricMetadata, metricCategories, disableCategories, groupByName, data, savedCustomColumnOrder,
    initialState, columnOrder, sortMetricId, sortDirection, isAbsoluteSort, sortArrowDirection, handleClickContributor,
    includeDrilldownArrows, isTopLevel, handleSetTableLevel, handleSortChange, parentLevelData,
    selectedTableLevels, currentLevelData, sortedContributorsArray, onDrilldownRowClick, intradayPricePrefix,
    handleGoToRow, currency, accountCurrency, isCurrencyUnavailable,
  ])

  const {
    columns,
    columnGroupingModel,
  } = useColumnDefinitions(columnProps)

  const { fieldExtractors, dataGridRemountKey } = useContributorsGridBase({ columns, rows })

  const { containerRef, maxLines, columnHeaderHeight, columnGroupHeaderHeight } = useHeaderLayout({ apiRef, columns, dataGridRemountKey })

  const downloadDataInput = useMemo(() => ({
    data: buildDownloadRows(currentLevelData, sortedContributorsArray),
    metrics,
  }), [currentLevelData, sortedContributorsArray, metrics])

  const baseRegistration = useMemo(() => ({
    key: downloadKey,
    sheetName: downloadName || "Data",
    getData: () => {
      const result = makeAssetViewDownloadData(downloadDataInput)
      return result ? result.data : null
    },
  }), [downloadKey, downloadName, downloadDataInput])

  const downloadRegistrations = useMemo(() => {
    if (!downloadKey) return null
    const registrations = []
    registrations.push(baseRegistration)

    const shouldAllowFlattenedValues = groupByName && isPositionAttributeGroup
    if (shouldAllowFlattenedValues) {
      registrations.push({
        ...baseRegistration,
        key: `${baseRegistration.key}.${groupByName}`,
        shouldFlatten: true,
        getData: () => {
          const result = makeAssetViewDownloadData({ ...downloadDataInput, shouldFlatten: true })
          return result ? result.data : null
        },
      })
    }
    return registrations
  }, [downloadKey, baseRegistration, downloadDataInput, groupByName, isPositionAttributeGroup])

  useRegisterDownloadData(downloadRegistrations)
  useColumnDragImage(apiRef)

  const getRowClassName = useCallback((params) => {
    const contributor = params.row.contributor
    const useDataZebraStriping = !contributor.notHeld
      && !contributor.isNoLongerHeld
      && !contributor.pinnedRowOrder
    const zebraStripeIsAlt = useDataZebraStriping && params.indexRelativeToCurrentPage % 2 === 1

    return classNames({
      "not-held": contributor.notHeld,
      "no-longer-held": contributor.isNoLongerHeld,
      pinned: contributor.pinnedRowOrder,
      unmodeled: contributor.isUnmodeled,
      "highlighted-row": params.row.isHighlighted,
      "aggregation-row": params.row.isAggregation,
      "drill-down": params.row.isAggregation && !isTopLevel,
      "sticky-aggregation": params.row.isAggregation,
      "row-zebra-base": useDataZebraStriping && !zebraStripeIsAlt,
      "row-zebra-alt": zebraStripeIsAlt,
      "drilldown-available": Boolean(onDrilldownRowClick) && !params.row.isAggregation && canContributorOpenDrilldown(contributor),
      "intraday-price-prior": isRowPricedOnPriorClose(params, visibleIntradayPricePrefix),
      "go-to-row-flash": params.row.id === flashingRowId,
    })
  }, [isTopLevel, onDrilldownRowClick, visibleIntradayPricePrefix, flashingRowId])

  const rowSlotProps = useMemo(
    () => ({ viewLabel, onDrilldownRow: onDrilldownRowClick }),
    [viewLabel, onDrilldownRowClick],
  )

  const loading = status === LOADING || (enablePersistence && !isStateLoaded)
  const getCellClassName = useCellValueFlash(rows, loading, fieldExtractors)
  const datagridTableType = gridId.split(".")[0]

  return (
    <Box ref={containerRef} className={classNames("contributors-datagrid", classes)} data-test-class={`${datagridTableType}-datagrid`}>
      <Box key={goToAnnouncementKey} component="span" sx={visuallyHidden} aria-live="polite" data-test-class="go-to-announcement">
        {goToAnnouncement}
      </Box>
      <HeaderMaxLinesContext value={maxLines}>
        <GoToFinderDataGrid
          key={dataGridRemountKey}
          sx={CONTRIBUTORS_GRID_SX}
          columnHeaderHeight={columnHeaderHeight}
          columnGroupHeaderHeight={columnGroupHeaderHeight}
          initialState={{
            density: "compact",
            pinnedColumns: { left: ["label", "description"] },
            ...omit(initialState, PERSISTED_STATE_KEYS_OMITTED_FOR_HYDRATION),
          }}
          autosizeOptions={{
            includeOutliers: false,
            includeHeaders: false,
            minWidth: DEFAULT_METRIC_COLUMN_WIDTH,
            maxWidth: MAX_COLUMN_WIDTH,
          }}
          apiRef={apiRef}
          loading={loading}
          rows={rows}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          pinnedRows={pinnedRows}
          sortingMode="server" // Disable client-side sorting
          columnResizeMode="onEnd"
          scrollbarSize={17}
          rowHeight={40}
          slots={{
            row: DrilldownTooltipRow,
            footer: TableFooter,
            columnMenu: CustomColumnMenu,
          }}
          slotProps={{
            row: rowSlotProps,
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "skeleton",
            },
            footer: {
              intradayPricingSummary,
            },
            toolbar: {
              isTopLevel,
              currentLevelLabel,
              handleSetTableLevel,
            },
            columnMenu: {
              onSortChange: handleSortChange,
              currentSortField: sortMetricId,
              currentSortDirection: sortDirection,
            },
            basePopper: {
              className: "contributors-datagrid-menu-popper",
            },
          }}
          getRowClassName={getRowClassName}
          getCellClassName={getCellClassName}
          hideFooterSelectedRowCount
          hideFooterRowCount={false}
          cellModesModel={{}}
          columnBufferPx={0}
          checkboxSelection={false}
          disableVirtualization={false}
          disableColumnResize={false}
          disableRowGrouping
          disableAggregation
          disableColumnSelector={false}
          disableDensitySelector={false}
          disableMultipleColumnsFiltering={false}
          disableExtendRowFullWidth={true}
          disableMultipleRowSelection
          disableRowSelectionOnClick
          disableColumnSorting
        />
      </HeaderMaxLinesContext>
    </Box>
  )
}

function ContributorsDataGridWithError({ status, errorMessage, ...props }) {
  // Leverage ErrorBoundary to catch errors and display a fallback component
  if (status === ERROR) {
    throw new Error(errorMessage ?? "")
  }

  return <ContributorsDataGridComponent status={status} {...props} />
}

ContributorsDataGridWithError.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, "success"]),
  errorMessage: PropTypes.string,
}

export default function ContributorsDataGrid({ status, ...props }) {
  const tradesResetKey = useTradesResetKey()

  return (
    <ErrorBoundary key={tradesResetKey} fallback={DataGridErrorFallback}>
      <ContributorsDataGridWithError status={status} {...props} />
    </ErrorBoundary>
  )
}

export { default as DrilldownContributorsDataGrid } from "./drilldown-panel"

ContributorsDataGrid.propTypes = {
  data: PropTypes.object.isRequired,
  status: PropTypes.oneOf([LOADING, ERROR, "success"]).isRequired,
  errorMessage: PropTypes.string,
  metrics: PropTypes.arrayOf(MetricPropType).isRequired,
  disableCategories: PropTypes.bool,
  groupByName: PropTypes.string,
  isPositionAttributeGroup: PropTypes.bool,
  defaultSort: PropTypes.shape({
    path: PropTypes.string.isRequired,
    direction: PropTypes.oneOf([ASC, DESC]).isRequired,
  }).isRequired,
  idLabel: PropTypes.string,
  classes: PropTypes.string,
  includeDrilldownArrows: PropTypes.bool,
  downloadKey: PropTypes.string,
  downloadName: PropTypes.string,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  intradayPricingSummary: IntradayPricingSummaryPropType,
  intradayPricePrefix: PropTypes.string,
  currency: PropTypes.string,
  accountCurrency: PropTypes.string,
  isCurrencyUnavailable: PropTypes.bool,
  selectedTableLevels: PropTypes.arrayOf(PropTypes.string),
  sortMetricId: PropTypes.string,
  onSortMetricIdChange: PropTypes.func,
  sortDirection: PropTypes.oneOf([ASC, DESC, "ASC_ABS", "DESC_ABS", null]),
  onSortDirectionChange: PropTypes.func,
  tableNavigationLevels: PropTypes.arrayOf(PropTypes.string),
  onTableNavigationChange: PropTypes.func,
  onSortChanged: PropTypes.func,
  onTableNavigationChanged: PropTypes.func,
  onRowClicked: PropTypes.func,
  onDrilldownRowClick: PropTypes.func,
  viewLabel: PropTypes.string,
  onPageChanged: PropTypes.func,
  onDataDownloaded: PropTypes.func,
  persistenceId: PropTypes.string,
  enablePersistence: PropTypes.bool,
  columnOrder: PropTypes.string,
  onColumnOrderChange: PropTypes.func,
}

ContributorsDataGridComponent.propTypes = ContributorsDataGrid.propTypes
